// Sprint 6 (Phase 3, D49) — TurnSummaryCard.
//
// Synthetic, render-time card emitted after each completed assistant turn
// (closed by a `system_event` with `subtype: 'result'`). NEVER persisted —
// this is a pure projection of the messages already in the timeline.
//
// Layout (founder mode):
//   ┌─────────────────────────────────────────────────────┐
//   │  ✅ Done                                             │
//   │                                                      │
//   │  • Modified 3 files in backend/auth                 │
//   │  • Type check passed (0 errors)                     │
//   │  • Tests passed (47/47)                             │
//   │                                                      │
//   │  Switched to Sonnet 4.5 mid-turn                    │
//   └─────────────────────────────────────────────────────┘
//
// Visual: see `docs/design-system.md` §9.AA.

import { useState } from 'react';
import { Icon, type IconName } from '../atoms/Icon';
import { useTranslation } from '../../i18n';
import type { TKey } from '../../i18n';
import type { TurnSummary, TurnVerification } from '../../lib/tool-rendering/synthesize-summary';

export interface TurnSummaryCardProps {
  summary: TurnSummary;
}

export function TurnSummaryCard({ summary }: TurnSummaryCardProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const headline = headlineFor(summary.status, t);
  const headlineIconName: IconName =
    summary.status === 'success' ? 'Check' : summary.status === 'error' ? 'X' : 'Square';
  const headlineColor =
    summary.status === 'success'
      ? 'text-status-success'
      : summary.status === 'error'
        ? 'text-status-error'
        : 'text-muted-foreground';

  return (
    <div
      className="fade-in-0 slide-in-from-top-2 animate-in card-summary px-4 py-3"
      data-testid="turn-summary-card"
      data-status={summary.status}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon name={headlineIconName} className={`shrink-0 ${headlineColor}`} size={16} aria-hidden="true" />
        <span className={`font-semibold text-body ${headlineColor}`}>{headline}</span>
      </div>

      <ul className="space-y-1 text-body text-muted-foreground leading-snug">
        {summary.filesModified && (
          <li data-testid="summary-files">
            • {summary.filesModified.commonDir
              ? t(
                  summary.filesModified.count === 1
                    ? 'turnSummary.modifiedFileIn'
                    : 'turnSummary.modifiedFilesIn',
                  { count: summary.filesModified.count, dir: summary.filesModified.commonDir },
                )
              : t(
                  summary.filesModified.count === 1
                    ? 'turnSummary.modifiedFile'
                    : 'turnSummary.modifiedFiles',
                  { count: summary.filesModified.count },
                )}
          </li>
        )}
        {summary.verifications.map((v, i) => (
          // Sprint messages-section-fixes (D-msg-7) — index-prefixed
          // composite key. Two verifications of the same `kind` with no
          // `rawCommand` (e.g. two `tests` rows) collided on the prior
          // key and produced React warnings.
          <VerificationLine
            key={`${i}-${v.kind}:${v.rawCommand ?? ''}`}
            v={v}
            t={t}
          />
        ))}
      </ul>

      {summary.systemNotes.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border/50 text-caption text-muted-foreground space-y-0.5">
          {summary.systemNotes.map((n, i) => (
            // Sprint messages-section-fixes (D-msg-7) — composite key
            // with index + content prefix to survive note reordering.
            <div key={`${i}-${n.slice(0, 40)}`}>{n}</div>
          ))}
        </div>
      )}

      {hasDeveloperDetail(summary) && (
        <div className="mt-2 pt-2 border-t border-border/50">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="text-caption text-muted-foreground/45 hover:text-muted-foreground inline-flex items-center gap-1"
            data-testid="summary-dev-toggle"
          >
            {open ? <Icon name="ChevronDown" size={12} /> : <Icon name="ChevronRight" size={12} />}
            {open ? t('turnSummary.hideCommands') : t('turnSummary.showCommands')}
          </button>
          {open && (
            <div
              className="mt-1 space-y-1 text-2xs font-mono text-muted-foreground/45" /* design-discipline: kept as text-2xs — developer-mode detail */
              data-testid="summary-dev-detail"
            >
              {summary.verifications
                .filter((v) => v.rawCommand)
                .map((v, i) => (
                  // D-msg-7 — composite key on filtered list to keep
                  // entries stable across re-renders when a verification
                  // is added/removed mid-turn.
                  <div key={`${i}-${v.kind}:${v.rawCommand}`}>
                    <span className={v.passed ? 'text-status-success' : 'text-status-error'}>
                      {v.passed ? '✓' : '✗'}
                    </span>{' '}
                    {v.rawCommand}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VerificationLine({ v, t }: { v: TurnVerification; t: (key: TKey, params?: Record<string, string | number>) => string }) {
  const label = labelFor(v.kind, t);
  const verb = v.passed ? t('turnSummary.passed') : t('turnSummary.failed');
  const metric = v.metric ? ` (${v.metric})` : '';
  const vIconName: IconName = v.passed ? 'Check' : 'X';
  const color = v.passed ? 'text-status-success' : 'text-status-error';
  return (
    <li className="flex items-center gap-1.5" data-testid={`summary-verification-${v.kind}`}>
      <Icon name={vIconName} className={`shrink-0 ${color}`} size={12} aria-hidden="true" />
      <span>
        {label} {verb}
        {metric}
      </span>
    </li>
  );
}

function headlineFor(s: TurnSummary['status'], t: (key: TKey) => string): string {
  if (s === 'success') return t('turnSummary.done');
  if (s === 'error') return t('turnSummary.errored');
  return t('turnSummary.stopped');
}

function labelFor(k: TurnVerification['kind'], t: (key: TKey) => string): string {
  if (k === 'typecheck') return t('turnSummary.typecheck');
  if (k === 'tests') return t('turnSummary.tests');
  if (k === 'lint') return t('turnSummary.lint');
  return t('turnSummary.build');
}

function hasDeveloperDetail(s: TurnSummary): boolean {
  return s.verifications.some((v) => v.rawCommand);
}
